import SEO from "@/components/SEO";
import { Reveal, Eyebrow, PageBand, CTABand, Photo } from "@/components/Shared";
import EnquiryForm from "@/components/EnquiryForm";
import { CLINIC, INSTAGRAM_URL } from "@/data/content";
import { PAGE_HEROES } from "@/data/photos";
import { MapPin, Mail, Car, Accessibility, Instagram, Train } from "lucide-react";

const Contact = () => (
  <>
    <SEO title="Contact & Location | Lumière Aesthetics, Cottesloe" description="Shop 3/40 Marine Parade, Cottesloe WA 6011. Free parking on Marine Parade, two minutes from the beach. Enquiries welcome." />
    <PageBand
      eyebrow="Contact"
      title="Find us"
      standfirst="Shop 3/40 Marine Parade, Cottesloe — on the oceanfront, two minutes from the beach."
      image={PAGE_HEROES.contact}
    />

    <section className="bg-warm-white" data-testid="contact-details">
      <div className="shell py-24 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <div className="space-y-8">
            <div className="flex gap-5">
              <MapPin className="w-5 h-5 text-gold mt-1 shrink-0" strokeWidth={1.25} />
              <div>
                <h3 className="text-deep-sea">Address</h3>
                <p className="text-ink/75 text-sm mt-2 leading-relaxed" data-testid="contact-address">{CLINIC.address}</p>
              </div>
            </div>
            <div className="flex gap-5">
              <Mail className="w-5 h-5 text-gold mt-1 shrink-0" strokeWidth={1.25} />
              <div>
                <h3 className="text-deep-sea">Email & phone</h3>
                <a href={`mailto:${CLINIC.email}`} data-testid="contact-email" className="text-ink/75 text-sm mt-2 block hover:text-gold transition-colors duration-300">
                  {CLINIC.email}
                </a>
                <a href="tel:+61863884474" data-testid="contact-phone" className="text-ink/75 text-sm mt-1 block hover:text-gold transition-colors duration-300">
                  {CLINIC.phone}
                </a>
              </div>
            </div>
            <div className="flex gap-5">
              <Instagram className="w-5 h-5 text-gold mt-1 shrink-0" strokeWidth={1.25} />
              <div>
                <h3 className="text-deep-sea">Instagram</h3>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="text-ink/75 text-sm mt-2 block hover:text-gold transition-colors duration-300">
                  @lumiereaesthetics_cottesloe
                </a>
              </div>
            </div>
            <div>
              <Eyebrow className="mb-3">Hours</Eyebrow>
              <p className="text-ink/75 text-sm" data-testid="contact-hours">{CLINIC.hoursText}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="relative h-full min-h-[400px]">
            <iframe
              title="Map — Lumière Aesthetics, 40 Marine Parade, Cottesloe"
              src="https://www.google.com/maps?q=40+Marine+Parade%2C+Cottesloe+WA+6011&z=16&output=embed"
              className="w-full h-full min-h-[400px] border-0 rounded-lg"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-cream" data-testid="contact-notes">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 sm:grid-cols-3 gap-10">
        {[
          { icon: Car, title: "Parking", text: "Free parking on Marine Parade, directly outside the practice." },
          { icon: Train, title: "Public transport", text: "Cottesloe station is a short walk; buses run along Marine Parade." },
          { icon: Accessibility, title: "Accessibility", text: "Step-free access throughout. Tell us your access needs when booking and we'll make the visit easy." },
        ].map((note, i) => (
          <Reveal key={note.title} delay={i * 0.08}>
            <div className="flex gap-4">
              <note.icon className="w-5 h-5 text-gold mt-1 shrink-0" strokeWidth={1.25} />
              <div>
                <h3 className="text-deep-sea">{note.title}</h3>
                <p className="text-ink/75 text-sm mt-2 leading-relaxed">{note.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-warm-white" data-testid="contact-form-section">
      <div className="max-w-2xl mx-auto px-8 py-24">
        <Reveal>
          <Eyebrow className="mb-4">Enquiries</Eyebrow>
          <h2 className="text-deep-sea mb-10">Write to the practice</h2>
          <EnquiryForm type="general" />
        </Reveal>
      </div>
    </section>

    <CTABand />
  </>
);

export default Contact;
